/** Player settings (docs/spec/07 §5), kept in localStorage. */

import type { CrtMode, ScaleMode } from '../render/display/display';
import type { KeyValueStore } from './storage';
import { KEYS } from './storage';

export type Action = 'up' | 'down' | 'left' | 'right' | 'fire' | 'pause';
export const ACTIONS: readonly Action[] = ['up', 'down', 'left', 'right', 'fire', 'pause'];

/** Two keys per action, as `KeyboardEvent.code`; '' is an empty slot. */
export type Bindings = Readonly<Record<Action, readonly [string, string]>>;

export const DEFAULT_BINDINGS: Bindings = {
  up: ['ArrowUp', 'KeyW'],
  down: ['ArrowDown', 'KeyS'],
  left: ['ArrowLeft', 'KeyA'],
  right: ['ArrowRight', 'KeyD'],
  fire: ['Space', 'KeyZ'],
  pause: ['Escape', 'KeyP'],
};

/** Keys that are always fire, whatever the bindings (docs/spec/07 §3). */
export const FIXED_FIRE = ['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight'];

export type Preset = 'enhanced' | 'remastered' | 'authentic' | 'custom';

/** C64: four colours a cell, fat pixels. Remastered: full-resolution shaded sprites (docs/spec/04 §8). */
export type Art = 'c64' | 'remastered';

export interface Options {
  readonly preset: Preset;
  readonly smooth: boolean;
  readonly particles: boolean;
  readonly shake: boolean;
  readonly wide: boolean;
  readonly crt: CrtMode;
  readonly art: Art;
  /** Remastered only: Digby's lamp, glinting diamonds, glowing creatures. */
  readonly lighting: boolean;
  readonly scale: ScaleMode;
  /** 0-10. */
  readonly master: number;
  readonly sfx: number;
  readonly music: number;
  readonly caveMusic: boolean;
  readonly speed: 'normal' | 'relaxed';
  readonly reduceFlashing: boolean;
  readonly highContrast: boolean;
  readonly touch: 'auto' | 'on' | 'off';
  readonly keys: Bindings;
}

type Look = Pick<Options, 'smooth' | 'particles' | 'shake' | 'wide' | 'crt' | 'art' | 'lighting'>;

const PRESETS: Record<'enhanced' | 'remastered' | 'authentic', Look> = {
  enhanced: { smooth: true, particles: true, shake: true, wide: true, crt: 'soft', art: 'c64', lighting: false },
  remastered: { smooth: true, particles: true, shake: true, wide: true, crt: 'soft', art: 'remastered', lighting: true },
  authentic: { smooth: false, particles: false, shake: false, wide: false, crt: 'off', art: 'c64', lighting: false },
};

const LOOK_KEYS: readonly (keyof Look)[] = ['smooth', 'particles', 'shake', 'wide', 'crt', 'art', 'lighting'];

export const DEFAULT_OPTIONS: Options = {
  preset: 'enhanced',
  ...PRESETS.enhanced,
  scale: 'pixel',
  master: 8,
  sfx: 8,
  music: 6,
  caveMusic: false,
  speed: 'normal',
  reduceFlashing: false,
  highContrast: false,
  touch: 'auto',
  keys: DEFAULT_BINDINGS,
};

/** Apply a preset's presentation settings. */
export function withPreset(o: Options, preset: Preset): Options {
  return preset === 'custom' ? { ...o, preset } : { ...o, ...PRESETS[preset], preset };
}

/** After changing a presentation setting by hand, the preset becomes Custom unless it still matches one. */
export function settlePreset(o: Options): Options {
  for (const name of ['enhanced', 'remastered', 'authentic'] as const) {
    const p = PRESETS[name];
    if (LOOK_KEYS.every((k) => p[k] === o[k])) return { ...o, preset: name };
  }
  return { ...o, preset: 'custom' };
}

function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function level(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(10, Math.max(0, Math.round(value))) : fallback;
}

function bindings(value: unknown): Bindings {
  const out: Record<Action, readonly [string, string]> = { ...DEFAULT_BINDINGS };
  if (typeof value !== 'object' || value === null) return out;
  for (const a of ACTIONS) {
    const v = (value as Record<string, unknown>)[a];
    if (Array.isArray(v) && v.length === 2 && v.every((k) => typeof k === 'string')) out[a] = [String(v[0]), String(v[1])];
  }
  return out;
}

export function parseOptions(json: string | null): Options {
  if (!json) return DEFAULT_OPTIONS;
  try {
    const r = JSON.parse(json) as Record<string, unknown>;
    const d = DEFAULT_OPTIONS;
    const bool = (k: keyof Options, f: boolean): boolean => (typeof r[k] === 'boolean' ? (r[k] as boolean) : f);
    return {
      preset: pick(r['preset'], ['enhanced', 'remastered', 'authentic', 'custom'] as const, d.preset),
      smooth: bool('smooth', d.smooth),
      particles: bool('particles', d.particles),
      shake: bool('shake', d.shake),
      wide: bool('wide', d.wide),
      crt: pick(r['crt'], ['off', 'soft', 'arcade'] as const, d.crt),
      art: pick(r['art'], ['c64', 'remastered'] as const, d.art),
      lighting: bool('lighting', d.lighting),
      scale: pick(r['scale'], ['pixel', 'fit'] as const, d.scale),
      master: level(r['master'], d.master),
      sfx: level(r['sfx'], d.sfx),
      music: level(r['music'], d.music),
      caveMusic: bool('caveMusic', d.caveMusic),
      speed: pick(r['speed'], ['normal', 'relaxed'] as const, d.speed),
      reduceFlashing: bool('reduceFlashing', d.reduceFlashing),
      highContrast: bool('highContrast', d.highContrast),
      touch: pick(r['touch'], ['auto', 'on', 'off'] as const, d.touch),
      keys: bindings(r['keys']),
    };
  } catch {
    return DEFAULT_OPTIONS;
  }
}

/** The live settings: read them, change them, hear about changes. */
export class OptionsStore {
  private value: Options;
  private readonly listeners = new Set<(o: Options) => void>();

  constructor(private readonly store: KeyValueStore) {
    this.value = parseOptions(store.load(KEYS.options));
  }

  get(): Options {
    return this.value;
  }

  set(next: Options): void {
    this.value = next;
    this.store.save(KEYS.options, JSON.stringify(next));
    for (const l of this.listeners) l(next);
  }

  update(change: Partial<Options>): void {
    this.set({ ...this.value, ...change });
  }

  /** Apply for this page load only (URL overrides such as `?fx=authentic`), without saving. */
  override(change: Partial<Options>): void {
    this.value = { ...this.value, ...change };
    for (const l of this.listeners) l(this.value);
  }

  subscribe(listener: (o: Options) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
